import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(timezone)

export const supportedDateFormats = [
  // ISO (YYYY and YY)
  'YYYY-MM-DD',
  'YY-MM-DD',
  'YYYY/MM/DD',
  'YY/MM/DD',
  'YYYY-MM-DDTHH:mm:ss[Z]',
  'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
  'YYYY/MM/DDTHH:mm:ss[Z]',
  'YYYY/MM/DDTHH:mm:ss.SSS[Z]',

  // US
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'MM/DD/YY',
  'M/D/YY',
  'MM-DD-YY',
  'M-D-YY',
  'MM/DD/YYYY HH:mm:ssZ',
  'MM-DD-YYYY HH:mm:ssZ',
  'MM/DD/YY HH:mm:ssZ',
  'MM/DD/YYYY h:mm:ss A',
  'M/D/YYYY h:mm:ss A',
  'MM/DD/YY h:mm:ss A',
  'M/D/YY h:mm:ss A',

  // EU
  'DD/MM/YYYY',
  'D/M/YYYY',
  'DD-MM-YYYY',
  'D-M-YYYY',
  'DD/MM/YY',
  'D/M/YY',
  'DD-MM-YY',
  'D-M-YY',
  'DD/MM/YYYY HH:mm:ssZ',
  'DD/MM/YYYY H:mm:ss[Z]',
  'DD/MM/YY HH:mm:ssZ',
  'DD/MM/YY HH:mm:ss[Z]',
  'D/M/YYYY h:mm:ss A',
  'D/M/YY h:mm:ss A',

  // Named month
  'D MMMM YYYY',
  'DD MMMM YYYY',
  'D MMM YYYY',
  'DD MMM YYYY',
  'D MMMM YY',
  'DD MMMM YY',
  'D MMM YY',
  'DD MMM YY',
  'MMMM D, YYYY',
  'MMM D, YYYY',

  'D MMMM YYYY HH:mm:ss',
  'D MMMM YY HH:mm:ss',
  'D MMMM YYYY HH:mm:ssZ',
  'D MMM YY HH:mm:ssZ',
  'D MMMM YYYY h:mm:ss A',
  'D MMM YY h:mm:ss A',

  // Timezone suffixes
  'D MMMM YYYY [UTC]',
  'D MMMM YYYY [GMT]',
  'D MMM YY [UTC]',
  'D MMM YY [GMT]',
  'D MMMM YYYY HH:mm:ss [UTC]',
  'D MMM YY HH:mm:ss [UTC]'
]

// build each piece for readability with regex of supportedDateFormats above
const isoPattern =
  '(?:\\d{2}|\\d{4})[\\/\\.-]\\d{1,2}[\\/\\.-]\\d{1,2}' +
  '(?:[T ]\\d{2}:\\d{2}:\\d{2}(?:\\.\\d{1,3})?Z)?'

const numericPattern =
  '\\d{1,2}[\\/\\-]\\d{1,2}[\\/\\-](?:\\d{2}|\\d{4})' +
  '(?: \\d{1,2}:\\d{2}:\\d{2}(?:Z| [AP]M))?'

const monthNames =
  'Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|' +
  'Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|' +
  'Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?'

// two ways to write named‑month dates: "21 April 2025" or "April 21, 2025"
const namedPattern =
  // day‑month‑year
  '(?:\\d{1,2} (?:' +
  monthNames +
  '),? \\d{2,4}' +
  // or month‑day‑year
  '|' +
  '(?:' +
  monthNames +
  ') \\d{1,2},? \\d{2,4}' +
  ')' +
  // optional time + suffix
  '(?: \\d{1,2}:\\d{2}:\\d{2}(?:\\.\\d{1,3})?' +
  '(?:Z| [AP]M| UTC| GMT)?)?'

// combine all three
const dateTimeRegex = new RegExp(
  '^(?:' + isoPattern + '|' + numericPattern + '|' + namedPattern + ')$',
  'i' // case‑insensitive for AM/PM, month names, UTC/GMT
)

// usage
function looksLikeSupportedDate(str) {
  return dateTimeRegex.test(str)
}

function isValidDateString(str) {
  return looksLikeSupportedDate(str) && !isNaN(new Date(str).getTime())
}

const isDate = (value) => {
  if (value === null || value === undefined) {
    return false
  }
  if (toString.call(value) === '[object Date]') {
    return true
  }

  if (typeof value.replace === 'function') {
    value.replace(/^\s+|\s+$/gm, '')
  }

  return isValidDateString(value)
}

export { isDate }
